import React, { useState } from 'react';
import { Image, Modal, Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import { Typography } from '../../theme';
import { LANGUAGE_LOCALES, useI18n } from '../../i18n';
import { useTheme } from '../../theme/useTheme';
import type { PartnerReview } from './reviewsApi';
import { reviewPalette, StarRow } from './StarRating';

// Long reviews start collapsed to this many lines.
const COLLAPSED_LINES = 5;
export const isLongReview = (text: string) => text.length > 280 || text.split('\n').length > COLLAPSED_LINES;

export type ReviewMenuOption = { key: string; label: string; icon: string; destructive?: boolean; onPress: () => void };

// Bottom options sheet (Android alerts allow only three buttons).
export function ReviewOptionsSheet({ visible, title, options, onClose }: { visible: boolean; title: string; options: ReviewMenuOption[]; onClose: () => void }) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const palette = reviewPalette(theme);
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel={t('common.cancel')}>
        <Pressable style={[styles.sheet, { backgroundColor: palette.sheet, borderColor: palette.border }]} onPress={() => undefined}>
          <LinearGradient
            colors={['rgba(45,212,215,0.12)', 'rgba(139,92,246,0.06)', 'rgba(242,125,38,0.12)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={StyleSheet.absoluteFill}
            pointerEvents="none"
          />
          <LinearGradient
            colors={['#2DD4D7', '#8B5CF6', '#F27D26']}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={styles.sheetReflectiveEdge}
            pointerEvents="none"
          />
          <View style={styles.sheetHeading}>
            <Text style={[styles.sheetTitle, { color: palette.text }]}>{title}</Text>
          </View>
          {options.map(option => (
            <TouchableOpacity
              key={option.key}
              style={[
                styles.sheetOption,
                { borderColor: option.destructive ? 'rgba(248,113,113,0.28)' : palette.border },
                option.destructive && styles.sheetOptionDestructive,
              ]}
              onPress={() => { onClose(); option.onPress(); }}
              accessibilityRole="button"
              testID={`review-option-${option.key}`}
            >
              <View style={[styles.sheetOptionIcon, option.destructive && styles.sheetOptionIconDestructive]}>
                <Icon name={option.icon} size={19} color={option.destructive ? '#F87171' : '#FFB938'} />
              </View>
              <Text style={[styles.sheetOptionText, { color: option.destructive ? '#F87171' : palette.text }]}>{option.label}</Text>
              <Icon name="chevron-right" size={17} color={option.destructive ? 'rgba(248,113,113,0.62)' : palette.faint} />
            </TouchableOpacity>
          ))}
          <TouchableOpacity style={[styles.sheetCancel, { backgroundColor: palette.surfaceStrong }]} onPress={onClose} accessibilityRole="button">
            <Text style={[styles.sheetCancelText, { color: palette.text }]}>{t('common.cancel')}</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

export default function ReviewCard({ review, isOwn, onMenu }: { review: PartnerReview; isOwn: boolean; onMenu: () => void }) {
  const { t, language } = useI18n();
  const { theme } = useTheme();
  const palette = reviewPalette(theme);
  const [expanded, setExpanded] = useState(false);
  const long = isLongReview(review.text);
  const date = review.createdAt
    ? new Date(review.createdAt).toLocaleDateString(LANGUAGE_LOCALES[language], { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  const initial = (review.reviewerName || '?').trim().slice(0, 1).toUpperCase();
  return (
    <View style={[styles.card, { backgroundColor: palette.surface, borderColor: isOwn ? 'rgba(255,185,56,0.45)' : palette.border }]} testID={`review-${review.id}`}>
      <View style={styles.header}>
        {review.reviewerPhotoUrl
          ? <Image source={{ uri: review.reviewerPhotoUrl }} style={styles.avatar} />
          : <View style={[styles.avatar, styles.avatarFallback]}><Text style={styles.avatarLetter}>{initial}</Text></View>}
        <View style={styles.headerCopy}>
          <Text style={[styles.name, { color: palette.text }]} numberOfLines={1}>
            {review.reviewerName}
            {isOwn ? <Text style={[styles.ownTag, { color: palette.accent }]}>{`  ${t('partnerReviews.yourReview')}`}</Text> : null}
          </Text>
          <View style={styles.metaRow}>
            <StarRow value={review.rating} size={12} />
            <Text style={[styles.date, { color: palette.faint }]} numberOfLines={1}>
              {date}{review.editedAt ? ` · ${t('partnerReviews.edited')}` : ''}
            </Text>
          </View>
        </View>
        <TouchableOpacity
          onPress={onMenu}
          hitSlop={10}
          style={styles.menuButton}
          accessibilityRole="button"
          accessibilityLabel={t('partnerReviews.menu')}
          testID={`review-menu-${review.id}`}
        >
          <Icon name="more-horizontal" size={18} color={palette.muted} />
        </TouchableOpacity>
      </View>
      {review.status !== 'published' ? (
        <Text style={[styles.notice, { color: palette.accent }]}>
          {review.status === 'hidden' ? t('partnerReviews.hiddenNotice') : t('partnerReviews.removedNotice')}
        </Text>
      ) : null}
      {review.text ? (
        <>
          <Text style={[styles.body, { color: palette.text }]} numberOfLines={long && !expanded ? COLLAPSED_LINES : undefined}>
            {review.text}
          </Text>
          {long ? (
            <TouchableOpacity onPress={() => setExpanded(value => !value)} hitSlop={6} accessibilityRole="button">
              <Text style={[styles.more, { color: palette.accent }]}>{expanded ? t('partnerReviews.showLess') : t('partnerReviews.readMore')}</Text>
            </TouchableOpacity>
          ) : null}
        </>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: { borderRadius: 18, borderWidth: 1, padding: 14, gap: 8 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  avatar: { width: 38, height: 38, borderRadius: 19 },
  avatarFallback: { alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(216,180,254,0.22)' },
  avatarLetter: { color: '#D8B4FE', fontSize: 16, fontWeight: Typography.extrabold },
  headerCopy: { flex: 1, minWidth: 0, gap: 3 },
  name: { fontSize: 14, fontWeight: Typography.extrabold },
  ownTag: { fontSize: 11, fontWeight: Typography.bold },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  date: { fontSize: 12, flexShrink: 1 },
  menuButton: { padding: 4 },
  notice: { fontSize: 12, lineHeight: 17, fontWeight: Typography.semibold },
  body: { fontSize: 14, lineHeight: 21 },
  more: { fontSize: 13, fontWeight: Typography.bold },
  backdrop: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(1,4,13,0.82)', padding: 12 },
  sheet: { borderRadius: 28, borderWidth: 1, paddingTop: 18, paddingHorizontal: 14, paddingBottom: 14, overflow: 'hidden' },
  sheetReflectiveEdge: { position: 'absolute', top: 0, left: 28, right: 28, height: 2, borderRadius: 2 },
  sheetHeading: { alignItems: 'center', paddingTop: 6, marginBottom: 16 },
  sheetTitle: { fontSize: 13, fontWeight: Typography.extrabold, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1.1 },
  sheetOption: { flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 58, paddingHorizontal: 12, borderWidth: 1, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.045)', marginBottom: 9 },
  sheetOptionDestructive: { backgroundColor: 'rgba(248,113,113,0.055)' },
  sheetOptionIcon: { width: 36, height: 36, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,185,56,0.10)' },
  sheetOptionIconDestructive: { backgroundColor: 'rgba(248,113,113,0.10)' },
  sheetOptionText: { flex: 1, fontSize: 15, fontWeight: Typography.semibold },
  sheetCancel: { minHeight: 52, borderRadius: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', alignItems: 'center', justifyContent: 'center', marginTop: 3 },
  sheetCancelText: { fontSize: 15, fontWeight: Typography.bold, textAlign: 'center' },
});
